import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const COOKIE_VERSION = '1.0'; // Increment this when cookie policy changes
const COOKIE_EXPIRY_DAYS = 30;

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const checkCookieConsent = () => {
      const consent = localStorage.getItem('cookieConsent');
      const consentVersion = localStorage.getItem('cookieConsentVersion');
      const consentTimestamp = localStorage.getItem('cookieConsentTimestamp');
      
      const isExpired = consentTimestamp && 
        (Date.now() - parseInt(consentTimestamp)) > (COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);

      const needsConsent = !consent || 
        consentVersion !== COOKIE_VERSION || 
        isExpired;

      setIsVisible(Boolean(needsConsent));
    };

    checkCookieConsent();

    // Check consent status when the tab becomes visible
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        checkCookieConsent();
      }
    });
  }, []);

  const saveConsent = (decision: 'accepted' | 'declined') => {
    localStorage.setItem('cookieConsent', decision);
    localStorage.setItem('cookieConsentVersion', COOKIE_VERSION);
    localStorage.setItem('cookieConsentTimestamp', Date.now().toString());

    // If accepted, initialize third-party services
    if (decision === 'accepted') {
      initializeThirdPartyServices();
    }

    setIsVisible(false);
  };

  const handleAccept = () => saveConsent('accepted');
  const handleDecline = () => saveConsent('declined');

  // Initialize your third-party services here
  const initializeThirdPartyServices = () => {
    // Example: Google Analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted'
      });
    }
    
    // Add other third-party initializations here
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 shadow-lg border-t border-gray-200 dark:border-gray-700 p-4 z-50 animate-slideUp">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 relative">
        <button 
          onClick={() => setIsVisible(false)}
          className="absolute -top-2 right-0 sm:right-2 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          aria-label="Close cookie consent"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex-1 pr-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">We use cookies</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            We use cookies and similar technologies to help personalize content, enhance your experience, and analyze our traffic. 
            Your consent will expire after {COOKIE_EXPIRY_DAYS} days.
          </p>
          <button 
            onClick={() => window.open('/privacy-policy', '_blank')}
            className="text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 mt-1"
          >
            Learn more about our cookie policy
          </button>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={handleDecline}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all transform hover:scale-105"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
} 